import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { logout, setNotifications, type MeResponse } from '../api';
import { Button, Disclaimer, PageHeader } from '../components/ui';
import { hapticNotif, showConfirm } from '../lib/telegram';

interface Props {
  me: MeResponse;
}

export function SettingsPage({ me }: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [notifEnabled, setNotifEnabled] = useState(me.notificationsEnabled);

  const notifMut = useMutation({
    mutationFn: (enabled: boolean) => setNotifications(enabled),
    onSuccess: () => {
      hapticNotif('success');
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const logoutMut = useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      hapticNotif('success');
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const handleToggleNotif = (enabled: boolean) => {
    setNotifEnabled(enabled);
    notifMut.mutate(enabled);
  };

  const handleLogout = async () => {
    const ok = await showConfirm(t('settings.logoutConfirm'));
    if (ok) logoutMut.mutate();
  };

  const handleLangChange = (lng: string) => {
    void i18n.changeLanguage(lng);
  };

  return (
    <div className="pt-6 pb-4">
      <PageHeader title={t('settings.title')} />

      {/* Notifications */}
      <section className={sectionCls}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display uppercase tracking-wider text-valo-white text-base">
              {t('settings.notifications')}
            </div>
            <p className="text-xs text-valo-muted mt-0.5">
              {t('settings.notificationsHint')}
            </p>
          </div>
          <Toggle checked={notifEnabled} onChange={handleToggleNotif} />
        </div>
      </section>

      {/* Language */}
      <section className={sectionCls}>
        <div className="font-display uppercase tracking-wider text-valo-white text-base mb-3">
          {t('settings.language')}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {(['en', 'ru'] as const).map((lng) => (
            <button
              key={lng}
              onClick={() => handleLangChange(lng)}
              className={`clip-corners-sm px-3 py-2 text-sm font-display uppercase tracking-widest transition-colors ${
                i18n.language === lng
                  ? 'bg-valo-red text-valo-white'
                  : 'border border-valo-navy-border text-valo-muted hover:text-valo-white'
              }`}
            >
              {lng}
            </button>
          ))}
        </div>
      </section>

      {/* Account */}
      {me.hasRiotLink && (
        <section className={sectionCls}>
          <div className="font-display uppercase tracking-wider text-valo-white text-base mb-3">
            {t('settings.account')}
          </div>
          <dl className="space-y-2 text-sm">
            <Row label={t('settings.region')} value={me.riotRegion?.toUpperCase() ?? '—'} />
            <Row
              label={t('settings.puuid')}
              value={<code className="font-mono text-[11px] break-all">{me.riotPuuid ?? '—'}</code>}
            />
          </dl>
          <Button
            variant="danger"
            className="mt-4 w-full"
            onClick={handleLogout}
            disabled={logoutMut.isPending}
          >
            {t('settings.logout')}
          </Button>
        </section>
      )}

      <div className="mt-6">
        <Disclaimer />
      </div>
    </div>
  );
}

const sectionCls =
  'clip-corners-sm border border-valo-navy-border bg-valo-navy-soft/50 p-4 mb-3';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-[10px] uppercase tracking-widest text-valo-muted shrink-0 pt-0.5">
        {label}
      </dt>
      <dd className="text-valo-white text-right">{value}</dd>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${
        checked ? 'bg-valo-red' : 'bg-valo-navy-border'
      }`}
    >
      <span
        aria-hidden="true"
        className={`inline-block h-5 w-5 rounded-full bg-valo-white shadow-sm transition-transform duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}